/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import * as Msdyn365 from '@msdyn365-commerce/core';
export class GetFranchiseData extends Msdyn365.CommerceEntityInput implements Msdyn365.IActionInput {
    public Latitude?: number;
    public Longitude?: number;
    public Radius?: number;

    constructor(_Latitude?: number, _Longitude?: number, _Radius?: number) {
        super({
            shouldCacheOutput: true,
            cacheObjectType: 'FranchiseDataCache',
            cacheKey: 'FranchiseDataCache'
        });
        this.Latitude = _Latitude;
        this.Longitude = _Longitude;
        this.Radius = _Radius;
    }
    public getCacheKey = () => `FranchiseDataCache`;
    public getCacheObjectType = () => 'FranchiseDataCache';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

export interface IFranchise {
    "id": string;
    "name": string;
    "address_line_1": string;
    "address_line_2": string;
    "city": string;
    "state_province": string;
    "postal_code": string;
    "country": string;
    "lat": string;
    "long": string;
    "distance": number;
    "website_url": string;
    "miles"?: number;
}

const _calculateDistance = (latitude1: any, longitude1: any, latitude2: any, longitude2: any) => {

    // The math module contains a function
    // named toRadians which converts from
    // degrees to radians.
    const long1 = longitude1 * Math.PI / 180;
    const long2 = longitude2 * Math.PI / 180;
    const lat1 = latitude1 * Math.PI / 180;
    const lat2 = latitude2 * Math.PI / 180;

    // Haversine formula
    const dlon = long2 - long1;
    const dlat = lat2 - lat1;
    const a = Math.pow(Math.sin(dlat / 2), 2)
        + Math.cos(lat1) * Math.cos(lat2)
        * Math.pow(Math.sin(dlon / 2), 2);
    const c = 2 * Math.asin(Math.sqrt(a));

    // Radius of earth
    // for kilometers=6371
    // for miles = 3956
    const r = 3956;

    // calculate the result
    return (c * r);
};

export const getFranchiseInput = (inputData: Msdyn365.ICreateActionContext<Msdyn365.IGeneric<Msdyn365.IAny>>): Msdyn365.IActionInput => {
    return new GetFranchiseData();
};


export async function getFranchise(input: GetFranchiseData, ctx: Msdyn365.IActionContext): Promise<IFranchise[]> {
    const jsonUrl = ctx.requestContext.app.config.sociFranchiseDataUrl || 'https://files-us-prod.cms.commerce.dynamics.com/cms/api/fknscsckgq/binary/MAcq1X!!';
    const franchiseDataJson = await Msdyn365.sendRequest<IFranchise[]>(jsonUrl, 'get');
    const franchiseStores: IFranchise[] = [];
    franchiseDataJson.data.map((franchise: IFranchise) => {
        const miles = _calculateDistance(input.Latitude, input.Longitude, franchise.lat, franchise.long);
        if (miles <= (input.Radius || 50)) {
            franchise.miles = miles;
            franchiseStores.push(franchise);
        }
    });

    return franchiseStores;
}
export default Msdyn365.createObservableDataAction({
    id: 'FranchiseData',
    action: <Msdyn365.IAction<any>>getFranchise,
    input: getFranchiseInput
});